/**
 * Pricing tier definitions for subscription plans
 */

export type PlanType = 'standard' | 'pro' | 'pro_trial' | 'team' | 'business';
export type PlanCategory = 'individual' | 'team';

export interface PlanFeature {
  text: string;
  included: boolean;
}

export interface PricingPlan {
  id: PlanType;
  name: string;
  category: PlanCategory;
  price: number;
  billingPeriod: 'month' | '2 weeks';
  description: string;
  features: string[];
  userCount?: string; // e.g., "5 contractors + 2 admins"
  storage: string;
  popular?: boolean;
  additionalUserPrice?: number;
  additionalUserStorage?: string;
}

export const INDIVIDUAL_PLANS: PricingPlan[] = [
  {
    id: 'standard',
    name: 'Standard',
    category: 'individual',
    price: 59,
    billingPeriod: 'month',
    description: 'Perfect for individual assessors starting out',
    storage: '50 GB',
    features: [
      'Floor plans, EPC & inventory tools',
      'Retrofit survey workflows',
      'View-only templates',
      '20 AI questions',
      '20 room scans',
      'PDF exports',
      '50 GB included storage'
    ]
  },
  {
    id: 'pro_trial',
    name: 'Pro Trial',
    category: 'individual',
    price: 0,
    billingPeriod: '2 weeks',
    description: '14-day trial of Pro features',
    storage: '250 GB',
    features: [
      'Trial ends 8th March 2026',
      'All Pro features included',
      '20 AI questions',
      'Unlimited room scans',
      'Editable & reusable templates',
      'White-label branding',
      'Advanced PDF exports',
      'Priority AI features & early access',
      '250 GB included storage'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    category: 'individual',
    price: 79,
    billingPeriod: 'month',
    description: 'For professionals who need advanced features',
    storage: '250 GB',
    popular: true,
    features: [
      'Everything in Standard',
      '200 AI questions',
      'Unlimited room scans',
      'Editable & reusable templates',
      'White-label branding',
      'Advanced PDF exports',
      'Priority AI features & early access',
      '250 GB included storage'
    ]
  }
];

export const TEAM_PLANS: PricingPlan[] = [
  {
    id: 'team',
    name: 'Team',
    category: 'team',
    price: 299,
    billingPeriod: 'month',
    description: 'Ideal for growing assessment teams',
    userCount: '5 contractors + unlimited admins',
    storage: '1 TB',
    additionalUserPrice: 59,
    additionalUserStorage: '250 GB',
    features: [
      '5 contractors + unlimited admins',
      'All Pro account features for every individual',
      'Job assignment & team dashboards',
      'Shared templates & branding',
      '1 TB shared storage',
      'Additional users: £59/month per contractor',
      '+250 GB storage per additional user'
    ]
  },
  {
    id: 'business',
    name: 'Business',
    category: 'team',
    price: 499,
    billingPeriod: 'month',
    description: 'Complete solution for established businesses',
    userCount: '10 contractors + unlimited admins',
    storage: '3 TB',
    popular: true,
    additionalUserPrice: 59,
    additionalUserStorage: '250 GB',
    features: [
      '10 contractors + unlimited admins',
      'All Pro account features for every individual',
      'Advanced team management',
      'Priority support',
      '3 TB shared storage',
      'Additional users: £59/month per contractor',
      '+250 GB storage per additional user'
    ]
  }
];

export const ALL_PLANS: PricingPlan[] = [
  ...INDIVIDUAL_PLANS,
  ...TEAM_PLANS
];

export const ADDITIONAL_USER_PRICING = {
  pricePerContractor: 59,
  storagePerUser: '250 GB',
  description: 'Add contractors to your team plan'
};

// Helper function to get plan by ID
export function getPlanById(planId: PlanType): PricingPlan | undefined {
  return ALL_PLANS.find(plan => plan.id === planId);
}

// Helper function to get plans by category
export function getPlansByCategory(category: PlanCategory): PricingPlan[] {
  return ALL_PLANS.filter(plan => plan.category === category);
}

// Helper function to format price
export function formatPrice(price: number): string {
  return `£${price}`;
}

// Helper function to format billing period
export function formatBillingPeriod(period: 'month' | '2 weeks'): string {
  return period === 'month' ? '/month' : '14 day trial';
}

// Helper function to get appropriate plans for account type
export function getPlansForAccountType(accountType: 'personal' | 'business'): PricingPlan[] {
  if (accountType === 'personal') {
    return INDIVIDUAL_PLANS;
  } else {
    return TEAM_PLANS;
  }
}
